import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { logoutUser } from '@/entities/user'
import { setUserEmailVerified } from '@/entities/user/sessionSlice'
import { fetchUserInfo } from '@/entities/user/thunks/fetchUserInfo'
import { UserInfoDto } from '@/shared/apis/fastmile/models'
import { isTokenExpired } from '@/shared/lib/JWTTokenHelpers'
import { useAppDispatch, useAppSelector } from '@/shared/store'

export type LoginRouteProps = {
  children: React.ReactNode
}

export const LoginRoute: React.FC<LoginRouteProps> = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [userInfo, setUserInfo] = useState<UserInfoDto>()
  const session = useAppSelector(state => state.session)
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchUserInfo())
      .unwrap()
      .then(uInfo => {
        setIsSuccess(true)
        setUserInfo(uInfo)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [dispatch])

  // Navigate to the default route if the user is already logged in
  useEffect(() => {
    const isSessionExpired = isTokenExpired(session.accessToken) || session.token === null

    const searchParams = new URLSearchParams(window.location.search)
    const hasEmailVerificationSuccess = searchParams.has('email-verification-success')
    const hasEmailVerificationFailed = searchParams.has('email-verification-failed')

    if (hasEmailVerificationSuccess) {
      dispatch(setUserEmailVerified(true))
      navigate('/onboarding', { replace: true })
    }

    if (hasEmailVerificationFailed) {
      dispatch(logoutUser())
    }
    else if (!isSessionExpired && !session.isEmailVerified) {
      navigate('/verify-email', { replace: true })
    }
    else if (!isLoading && isSuccess && userInfo !== null && userInfo?.client.isOnboarded && !isSessionExpired) {
      navigate('/onboarding', { replace: true })
    }
    else if (!isLoading && !isSessionExpired && session.isEmailVerified) {
      navigate('/operations', { replace: true })
    }
  }, [dispatch, navigate, userInfo, isSuccess, session, isLoading])

  return (
    <>
      {children}
    </>
  )
}